// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Flex, Button, Box, Image, Grid, Text, Switch, Spinner, 
  AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, 
	useDisclosure, Input, Textarea, Accordion } from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { IoLocationSharp, IoClose, IoPeople } from 'react-icons/io5';
import { TbRainbow } from 'react-icons/tb';
import { MdWork } from 'react-icons/md';
import { FaGenderless, FaRuler, FaAmericanSignLanguageInterpreting, FaReact } from 'react-icons/fa';
import { MdOutlineInterests } from 'react-icons/md';
import { TbEdit } from 'react-icons/tb';
import { FaCheck } from 'react-icons/fa6';
import { GiGemini, GiAries, GiTaurus, GiCancer, GiLeo, 
  GiVirgo, GiLibra, GiScorpio, GiSagittarius, GiCapricorn, 
  GiAquarius, GiPisces } from 'react-icons/gi';
import { SiGotomeeting } from 'react-icons/si';
import { MdEmojiPeople } from 'react-icons/md';
import { MdVerified } from 'react-icons/md';
import { useQuery } from 'react-query';
import { LiaBirthdayCakeSolid } from 'react-icons/lia';

// Assets
import { getAge, parseDateBirthday, calculateProgress } from 'services/Utils';
import { apiService } from 'services/ApiService';
import placeholderUser from 'assets/img/placeholder-user.png';
import { textLang } from 'services/Lang';

// Components
import EditField from 'components/Form/EditField';
import EditSelect from 'components/Form/EditSelect';
import ErrorCard from 'components/Form/Errors';
import ProfileDetails from './ProfileDetails';
import ProfileIFind from './ProfileIFind';
import ProfileGallery from './ProfileGallery';
import UpdateAvatarModal from './ProfileAvatar';

const Profile = ({
	object, 
	setObject,
	findAgeFrom, setFindAgeFrom,
	findAgeTo, setFindAgeTo, refetchUser
}) => {
	const active = 2;
	const textColor = '#0E1633';

	const { isOpen, onOpen, onClose } = useDisclosure();

	const history = useHistory();
	const handleGallery = () => {
	    history.push('/profile/gallery'); 
	};

	//const [editDesc, setEditDesc] = useState(false);
	//const [editCountryAndCity, setEditCountryAndCity] = useState(false);
	const [editIFind, setEditIFind] = useState(false);
	//const [editMyCard, setEditMyCard] = useState(false);

	//const [object, setObject] = useState();
	const [objectErrors, setObjectErrors] = useState([]);
	//const [findAgeFrom, setFindAgeFrom] = useState();
	//const [findAgeTo, setFindAgeTo] = useState();
	const [isError, setIsError] = useState(false);

	const setData = useCallback((maxValue, target, key) => {
		if (target.length < maxValue) {
		  setObjectErrors([...objectErrors, key]);
		} else {
		  setObjectErrors(objectErrors.filter(error => error !== key));
		}
		setObject({...object, [key]: target}); 
	}, [object, objectErrors, setObject, setObjectErrors]);

	const handleField = useMemo(() => {
		const fieldConfigs = [
		  { field: 'description', maxValue: 1 },
		  { field: 'birthday', maxValue: 1 },
		  { field: 'career', maxValue: 0 },
		  { field: 'city', maxValue: 1 },
		  { field: 'country', maxValue: 1 },
		  //{ field: 'findAge', maxValue: 1 },
		  { field: 'findFor', maxValue: 1 },
		  { field: 'findGender', maxValue: 1 },
		  { field: 'firstName', maxValue: 1 },
		  { field: 'gender', maxValue: 1 },
		  { field: 'genderOfInterest', maxValue: 1 },
		  { field: 'interests', maxValue: 0 },
		  /*{ field: 'isHidden', maxValue: 1 },*/
		  { field: 'lastName', maxValue: 1 },
		  //{ field: 'questions', maxValue: 1 },
		  { field: 'relationship', maxValue: 0 },
		  { field: 'religion', maxValue: 0 },
		  { field: 'size', maxValue: 0 },
		];

		setIsError();

		return fieldConfigs.reduce((acc, { field, maxValue }) => {
		  acc[field] = (e) => setData(maxValue, e.target.value, field);
		  return acc;
		}, {});
	}, [setData]);

	useMemo(() => {
		refetchUser();
	}, []);

	const toggleInterest = (interest) => {
	  setObject((prev) => {
	    const updatedInterests = prev?.interests?.includes(interest)
	      ? prev?.interests?.filter((i) => i !== interest)
	      : [...prev?.interests, interest];

	    return { ...prev, interests: updatedInterests };
	  });
	};

	const handleUpdateObject = useCallback(async () => {
		setIsError();
		if (objectErrors.length > 0) {
      setIsError(true);
      return;
    }

		try {
			delete object.image;
			const response = await apiService.updateMe(object);
			if(response.data === false || response.statusCode !== 200){
				setIsError(response.message);
				return;
			}

			refetchUser();
		} catch(e){
			setIsError(true);
			return;
		}
	});

	return (
		<Box 
			maxH="100vh" 
			pt="16px" 
			pb="350px" 
			overflowY="auto">

			{ isError && <ErrorCard error={isError} mt="0px" /> }

			<Box bg="white" p="20px" borderRadius="16px">
				<Flex gap="12px" pb="16px" alignItems="center" position="relative">
					<Box w="92px" h="92px" position="relative" borderRadius="full">
						<Text
							onClick={onOpen} 
							bg="#ff4133"
					    color="white"
					    fontSize="12px"
					    w="100%"
					    h="28px"
					    p="6px 0px 0px 0px"
					    textAlign="center"
					    borderRadius="full"
					    fontWeight="700"
					    bottom="0"
							position="absolute">{textLang('edit')}</Text>
						<Image src={object?.image ?? placeholderUser} border="4px solid #ff4133" borderRadius="full" w="100%" h="100%" objectFit="cover" />
					</Box>

					<Box overflow="visible" w="50%">
						<Flex gap="8px" alignItems="center">
							<Text variant="title-lg" fontSize="24px">
								{ object?.firstName || 'Unknown' } { object?.lastName || '' }, {getAge(object?.birthday)}
							</Text>
						</Flex>

						{/*<Box
							bg="#ff4133"
						    borderRadius="30px"
						    color="white"
						    padding="4px 15px 4px 35px"
						    marginLeft="-41px"
						    width="fit-content"
						    marginTop="6px">
							90% complete
						</Box>*/}

						<Text fontSize="16px" >{object?.description}</Text>
					</Box>
				</Flex>

				<Accordion defaultIndex={[1]} allowToggle>
				  <AccordionItem style={{
				  	borderWidth: '0px'
				  }}>
				    <h2>
				      <AccordionButton 
				      _focus={{
				      	boxShadow: 'transparent',
				      }}
				      style={{
				      	background: 'transparent',
    						padding: '0px 0'
				     	}}>
				        <Box flex="1" textAlign="left">
				          <Text variant="title-lg">{textLang('about_me')}</Text>

				          {/*<Box 
                    m="8px 0px 4px 0px"
                    position="relative" 
                    h="6px" 
                    w="100%" 
                    borderRadius="24px" 
                    bg="#f1f1f1" 
                    overflow="hidden">
                      <Box 
                        bg="linear-gradient(180deg, #ff9a93 0%, #ff4133 100%)" 
                        w={() => calculateProgress(17,20)} 
                        h="6px" left="0" top="0" />
                  </Box>*/}
				        </Box>
				        <AccordionIcon fontSize="40px" style={{marginTop: '-6px'}} />
				      </AccordionButton>
				    </h2>
				    <AccordionPanel p="0 0 16px 0">
				      <ProfileDetails
				        object={object}
				        handleUpdateObject={handleUpdateObject}
				        handleField={handleField}
				        toggleInterest={toggleInterest}
				      />
				    </AccordionPanel>
				  </AccordionItem>
				  <AccordionItem style={{
				  	borderWidth: '0px'
				  }}>
				    <h2>
				      <AccordionButton 
				      _focus={{
				      	boxShadow: 'transparent',
				      }}
				      style={{
				      	background: 'transparent',
    						padding: '12px 0 0px 0'
				     	}}>
				        <Box flex="1" textAlign="left">
				          <Text variant="title-lg">{textLang('i_find')}</Text>

				          {/*<Box 
                    m="8px 0px 4px 0px"
                    position="relative" 
                    h="6px" 
                    w="100%" 
                    borderRadius="24px" 
                    bg="#f1f1f1" 
                    overflow="hidden">
                      <Box 
                        bg="linear-gradient(180deg, #ff9a93 0%, #ff4133 100%)" 
                        w={() => calculateProgress(3,20)} 
                        h="6px" left="0" top="0" />
                  </Box>*/}
				        </Box>
				        <AccordionIcon fontSize="40px" style={{marginTop: '-6px'}} />
				      </AccordionButton>
				    </h2>
				    <AccordionPanel p="0 0 0 0">
				      <ProfileIFind 
								object={object} 
								setObject={setObject}
								handleUpdateObject={handleUpdateObject} 
								handleField={handleField}
								findAgeFrom={findAgeFrom}
								findAgeTo={findAgeTo}
								setFindAgeFrom={setFindAgeFrom}
					      setFindAgeTo={setFindAgeTo} />
				    </AccordionPanel>
				  </AccordionItem>
				</Accordion>
			</Box>

			<UpdateAvatarModal 
        userData={object} 
        setUserData={setObject}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        />

			<ProfileGallery object={object} />
		</Box>
	);
};

export default Profile;